// B-70 — "Panel de cierres" grouped by date: the first screen of a client whose reports cover all
// its offices at once. One line per closing date — its offices counted and its daily documents —
// and a click opens the usual list (`PanelCierres`) on that date. It wears the list's own header,
// toolbar, table and pager, so the two read as one screen in two depths.
//
// Every date in the database is reachable: `getCierreClosingDates` answers a page of dates (25, 50
// or 100) with the total, and each date already summarised — one request per page, never one per
// date. A day nobody processed says so, with "Procesar día" opening "Nuevo cierre" on that date.
//
// It opens on the last 7 days, and "Descargar Matriz" hands over the Matriz — the same workbook a
// day's detail downloads — for every day of the range on screen, oldest first, a year at most.
import { useEffect, useMemo, useState } from "react";
import { ChevronRight, Download, RotateCcw, Upload } from "lucide-react";
import { Button, Spinner, cn } from "@mantis-sas/ui";

import { getCierreClosingDates, getExtractionFields } from "../../../ApiCalls/mantisBusiness";
import { restarDias } from "../../../utils/cierrePanel";
import { nombreDeAlerta, nombreDeFuente } from "../../../utils/cierreNombres";
import { descargarMatriz } from "../../../utils/cierreExcel";
import { diasDelRango, matrizDeRango } from "../../../utils/cierreMatrizDelDia";
import { ETIQUETA_DOCUMENTO, resumenDeFecha } from "../../../utils/cierrePanelFechas";
import { PERMISO, puede } from "../../../utils/cierrePermisos";
import { useNombresCierre } from "../../../hooks/useNombresCierre";
import { useAccesoCierres } from "../../../hooks/useAccesoCierres";
import { CierreManualDialog } from "./CierreManualDialog";
import { NuevoCierre } from "./NuevoCierreDialog";
import { Paginacion } from "./Paginacion";
import { TituloCierres } from "./TituloCierres";
import "./styles/brinksTheme.css";
import { guia } from "../../../utils/cierreTours";

const PUNTO_DOCUMENTO = {
  recibido: "bg-brinks-success",
  faltante: "bg-brinks-warning",
  error: "bg-brinks-danger",
};

// Each page and each client's status words, kept across visits: coming back from a day's detail
// redraws at once while the page is asked again behind it.
const cacheDePaginas = new Map();
const cacheDeEstados = new Map();

// For the tests, which render the panel again and again.
export const olvidarPaginas = () => {
  cacheDePaginas.clear();
  cacheDeEstados.clear();
};

export function PanelCierresPorFecha({ token, mantisBusinessId, clientCode, hoy, rango = null, onCambiarRango, onVerDetalle }) {
  // Every date until the person picks a range ("Todo"); a chosen shortcut follows `hoy`, which
  // moves once the client's timezone arrives. The range chosen is the page's (`rango`, kept in the
  // URL by `PanelCierresInicio`), so coming back from a day's detail keeps it.
  const rangoElegido = rango;
  const setRangoElegido = onCambiarRango;
  const desde = rangoElegido ? rangoElegido.desde : "";
  const hasta = rangoElegido ? rangoElegido.hasta : "";
  const setDesde = (d) => setRangoElegido({ desde: d, hasta });
  const setHasta = (h) => setRangoElegido({ desde, hasta: h });
  const [descarga, setDescarga] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(25);
  const [respuesta, setRespuesta] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [byState, setByState] = useState(() => cacheDeEstados.get(`${mantisBusinessId}|${clientCode}`) || null);
  const [manual, setManual] = useState(null);
  const [pedido, setPedido] = useState(null);
  const [recarga, setRecarga] = useState(0);

  const nombres = useNombresCierre(token, mantisBusinessId, clientCode);
  const acceso = useAccesoCierres();
  const puedeAbrir = puede(acceso, PERMISO.CREAR_CIERRES) || puede(acceso, PERMISO.PROCESAR_DIA);
  const puedeProcesar = puede(acceso, PERMISO.PROCESAR_DIA, { cliente: clientCode, entero: true });
  // The day's detail exports with the same permission; `matrizDeRango` writes only the rows it reaches.
  const puedeExportar = puede(acceso, PERMISO.EXPORTAR, { cliente: clientCode });
  const rangoDescarga = diasDelRango(desde, hasta);
  const descargando = descarga?.estado === "descargando";

  const descargarRango = async () => {
    setDescarga({ estado: "descargando", hechos: 0, total: rangoDescarga.dias?.length || 0 });
    try {
      const hoja = await matrizDeRango({
        token,
        mantisBusinessId,
        clientCode,
        desde,
        hasta,
        acceso,
        nombrar: (clave) => nombreDeAlerta(nombres, clave),
        onAvance: (hechos, total) => setDescarga({ estado: "descargando", hechos, total }),
      });
      await descargarMatriz(hoja);
      setDescarga(null);
    } catch (e) {
      setDescarga({ estado: "error", mensaje: e?.message || "No se pudo generar la Matriz." });
    }
  };

  // The client's own status words, which decide what "Cuadre OK" is — as in the detail.
  useEffect(() => {
    const clave = `${mantisBusinessId}|${clientCode}`;
    if (cacheDeEstados.has(clave)) return undefined;
    let activo = true;
    getExtractionFields(token, mantisBusinessId, clientCode)
      .then((campos) => {
        const mapa = campos?.statuses?.byState || null;
        if (mapa) cacheDeEstados.set(clave, mapa);
        if (activo) setByState(mapa);
      })
      .catch(() => {});
    return () => {
      activo = false;
    };
  }, [token, mantisBusinessId, clientCode]);

  // A new range or page size starts again at the first page.
  useEffect(() => {
    setPagina(1);
  }, [desde, hasta, porPagina]);

  useEffect(() => {
    const pedidoPagina = {
      since: desde || undefined,
      until: hasta || undefined,
      limit: porPagina,
      offset: (pagina - 1) * porPagina,
    };
    const clave = JSON.stringify([mantisBusinessId, clientCode, pedidoPagina]);
    const recordada = cacheDePaginas.get(clave);
    let activo = true;
    setError(false);
    if (recordada) {
      setRespuesta(recordada);
      setCargando(false);
    } else {
      setCargando(true);
    }
    (async () => {
      const r = await getCierreClosingDates(token, mantisBusinessId, clientCode, pedidoPagina).catch(() => null);
      if (!activo) return;
      if (r?.success) {
        cacheDePaginas.set(clave, r.data);
        setRespuesta(r.data);
      } else if (!recordada) {
        setRespuesta(null);
        setError(true);
      }
      setCargando(false);
    })();
    return () => {
      activo = false;
    };
  }, [token, mantisBusinessId, clientCode, desde, hasta, pagina, porPagina, recarga]);

  // Something changed on the server (a day processed, a closing opened): every page is stale.
  const recargar = () => {
    cacheDePaginas.clear();
    setRecarga((n) => n + 1);
  };

  const lineas = useMemo(() => (respuesta?.dates || []).map((dia) => resumenDeFecha(dia, byState)), [respuesta, byState]);
  const total = respuesta?.total || 0;
  const totalPaginas = Math.max(1, Math.ceil(total / porPagina));

  const atajos = [
    { etiqueta: "Todo", activo: !desde && !hasta, onClick: () => setRangoElegido(null) },
    { etiqueta: "Hoy", activo: desde === hoy && hasta === hoy, onClick: () => setRangoElegido({ desde: hoy, hasta: hoy }) },
    { etiqueta: "7 días", activo: desde === restarDias(hoy, 6) && hasta === hoy, onClick: () => setRangoElegido({ desde: restarDias(hoy, 6), hasta: hoy }) },
    { etiqueta: "Mes", activo: desde === restarDias(hoy, 29) && hasta === hoy, onClick: () => setRangoElegido({ desde: restarDias(hoy, 29), hasta: hoy }) },
  ];

  return (
    <div className="brinks-scope font-redhat flex min-h-0 flex-col gap-2 bg-background p-3 sm:p-4">
      <header className="mb-1 flex flex-wrap items-end justify-between gap-2">
        <div>
          <TituloCierres />
          <p className="mt-0.5 text-[13px] text-brinks-muted-foreground">
            Cierres por fecha — abre un día para ver el cuadre de cada oficina
          </p>
        </div>
        {puedeAbrir ? (
          <NuevoCierre
            token={token}
            mantisBusinessId={mantisBusinessId}
            clientCode={clientCode}
            pedido={pedido}
            onOpened={(abierto) => {
              setManual({ closingId: abierto.closingId, aviso: abierto.warning?.message || null, region: abierto.region });
              if (abierto.created) recargar();
            }}
            onDiaProcesado={recargar}
          />
        ) : null}
      </header>

      <section {...guia("Filtros", "Elige el periodo (Todo, Hoy, 7 días, Mes o un rango de fechas). Dentro de un día también buscas una oficina y filtras por estado.")} className="flex flex-wrap items-center gap-1.5 rounded-[10px] border bg-card p-1.5 shadow-brinks-card">
        <div className="grid grid-cols-4 gap-1">
          {atajos.map((a) => (
            <Button key={a.etiqueta} size="sm" className="h-9 px-3 text-[12.5px]" variant={a.activo ? "default" : "outline"} onClick={a.onClick}>
              {a.etiqueta}
            </Button>
          ))}
        </div>
        {/* The two dates take the bar's free width, each with its word beside the value. */}
        <label className="flex h-9 min-w-[180px] flex-1 items-center gap-2 rounded-[7px] border bg-card px-3 text-[12.5px] transition focus-within:border-brinks-border-strong">
          <span className="shrink-0 text-brinks-muted-foreground">Desde</span>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="min-w-0 flex-1 bg-transparent outline-none" aria-label="Fecha inicial" />
        </label>
        <label className="flex h-9 min-w-[180px] flex-1 items-center gap-2 rounded-[7px] border bg-card px-3 text-[12.5px] transition focus-within:border-brinks-border-strong">
          <span className="shrink-0 text-brinks-muted-foreground">Hasta</span>
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="min-w-0 flex-1 bg-transparent outline-none" aria-label="Fecha final" />
        </label>
        <Button size="sm" variant="ghost" className="h-9 gap-1.5 px-3 text-[12.5px]" onClick={() => setRangoElegido(null)}>
          <RotateCcw className="h-3.5 w-3.5" /> Restablecer
        </Button>
        {puedeExportar ? (
          <Button
 {...guia("Descargar Matriz", "Descarga en Excel la Matriz de todos los días del rango que estás viendo.")}            size="sm"
            className="h-9 gap-1.5 px-3.5 text-[12.5px]"
            disabled={!!rangoDescarga.error || descargando}
            title={rangoDescarga.error || `Descargar la Matriz del ${desde} al ${hasta}`}
            onClick={descargarRango}
          >
            {descargando ? <Spinner size="sm" /> : <Download className="h-3.5 w-3.5" />}
            {descargando ? `Descargando… ${descarga.hechos} de ${descarga.total} días` : "Descargar Matriz"}
          </Button>
        ) : null}
      </section>
      {puedeExportar && descarga?.estado === "error" ? (
        <p role="alert" className="-mt-1 px-1 text-[12px] text-brinks-danger">
          {descarga.mensaje}
        </p>
      ) : null}
      {puedeExportar && !descargando && rangoDescarga.error ? (
        <p className="-mt-1 px-1 text-[12px] text-brinks-muted-foreground">{rangoDescarga.error}</p>
      ) : null}

      {cargando ? (
        <div className="grid place-items-center rounded-[10px] border border-dashed bg-card py-14">
          <Spinner size="lg" />
        </div>
      ) : (
        <section {...guia("Lista", "Toca una fila para abrirla: un día muestra sus oficinas y una oficina muestra su cierre con documentos, cifras y alertas.")} className="flex min-w-0 flex-col overflow-hidden rounded-[10px] border bg-card shadow-brinks-card">
          <div className="min-w-0 overflow-x-auto">
            <table className="w-full min-w-[960px] table-fixed text-[12px] xl:text-[13px]" aria-label="Cierres por fecha">
              <colgroup>
                <col className="w-[9%]" />
                <col className="w-[7%]" />
                <col className="w-[8%]" />
                <col className="w-[8%]" />
                <col className="w-[9%]" />
                <col className="w-[9%]" />
                <col />
                <col className="w-[112px]" />
                <col className="w-[40px]" />
              </colgroup>
              <thead className="bg-brinks-surface">
                <tr className="whitespace-nowrap border-b text-left text-[10.5px] uppercase tracking-[0.04em] text-brinks-muted-foreground">
                  <th className="px-3 py-2 font-semibold">Fecha</th>
                  <th className="px-3 py-2 text-right font-semibold">Oficinas</th>
                  <th className="px-3 py-2 text-right font-semibold">Cuadre OK</th>
                  <th className="px-3 py-2 text-right font-semibold">Con alerta</th>
                  <th className="px-3 py-2 text-right font-semibold" title="Oficinas a las que les falta algún reporte del día">Sin reportes</th>
                  <th className="px-3 py-2 text-right font-semibold" title="Con sus reportes, sin alerta, todavía sin cuadrar">Por revisar</th>
                  <th className="px-3 py-2 font-semibold">Documentos</th>
                  <th className="px-3 py-2"><span className="sr-only">Acción</span></th>
                  <th className="px-3 py-2"><span className="sr-only">Ver detalle</span></th>
                </tr>
              </thead>
              <tbody>
                {lineas.map((l) => (
                  <tr
                    key={l.fecha}
                    data-fecha={l.fecha}
                    onClick={() => onVerDetalle(l.fecha)}
                    className="group h-[44px] cursor-pointer border-b transition last:border-0 hover:bg-brinks-surface-hover"
                  >
                    <td className="whitespace-nowrap px-3 font-medium tabular-nums text-brinks-foreground">{l.fechaLabel}</td>
                    <td className="px-3 text-right tabular-nums">{l.total}</td>
                    {/* A day nobody processed keeps every column in place: nothing to count, and
                        in Documentos what it is and what to do about it. */}
                    <Cifra valor={l.sinProcesar ? null : l.ok} tono="text-brinks-success" />
                    <Cifra valor={l.sinProcesar ? null : l.alerta} tono="text-brinks-danger" />
                    <Cifra valor={l.sinProcesar ? null : l.sinReportes} tono="text-brinks-warning" />
                    <Cifra valor={l.sinProcesar ? null : l.porRevisar} tono="text-brinks-foreground" />
                    <td className="px-3">
                      {l.sinProcesar ? (
                        <span className="rounded-full border bg-brinks-surface px-2 py-0.5 text-[11px] text-brinks-muted-foreground">
                          Sin procesar
                        </span>
                      ) : (
                        <Documentos documentos={l.documentos} nombres={nombres} fechaLabel={l.fechaLabel} />
                      )}
                    </td>
                    {/* Its own column, so every button lines up under the next. */}
                    <td className="px-3 text-right">
                      {l.sinProcesar && puedeProcesar ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1.5 px-2.5 text-[11.5px]"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPedido({ fecha: l.fecha, clientCode });
                          }}
                        >
                          <Upload className="h-3.5 w-3.5" /> Procesar
                        </Button>
                      ) : puedeProcesar ? (
                        // B-101 — a processed day is redone from here, with its files sent again (or
                        // only an office's late ones), without entering the day or any closing.
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1.5 px-2.5 text-[11.5px]"
                          title="Sube de nuevo los archivos del día, o solo los de una oficina."
                          onClick={(e) => {
                            e.stopPropagation();
                            setPedido({ fecha: l.fecha, clientCode });
                          }}
                        >
                          <RotateCcw className="h-3.5 w-3.5" /> Rehacer
                        </Button>
                      ) : null}
                    </td>
                    <td className="px-3 text-brinks-muted-foreground">
                      <ChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:text-brinks-foreground" aria-label={`Ver detalle del ${l.fechaLabel}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {lineas.length === 0 ? (
              <p className="p-8 text-center text-sm text-brinks-muted-foreground">
                {error ? "No se pudieron leer las fechas de cierre." : "No hay cierres para las fechas seleccionadas."}
              </p>
            ) : null}
          </div>
          {total > 0 ? (
            <Paginacion
              inicio={(pagina - 1) * porPagina}
              porPagina={porPagina}
              total={total}
              paginaActual={pagina}
              totalPaginas={totalPaginas}
              onPorPagina={setPorPagina}
              onPagina={setPagina}
              extra={
                <span className="hidden items-center gap-3 sm:inline-flex">
                  {Object.entries(PUNTO_DOCUMENTO).map(([estado, color]) => (
                    <span key={estado} className="inline-flex items-center gap-1">
                      <span className={cn("h-1.5 w-1.5 rounded-full", color)} aria-hidden="true" /> {ETIQUETA_DOCUMENTO[estado]}
                    </span>
                  ))}
                </span>
              }
            />
          ) : null}
        </section>
      )}

      <CierreManualDialog
        closingId={manual?.closingId || null}
        aviso={manual?.aviso}
        region={manual?.region}
        token={token}
        mantisBusinessId={mantisBusinessId}
        onOpenChange={(open) => !open && setManual(null)}
        onChanged={recargar}
      />
    </div>
  );
}

// A count drawn in its colour only when it is not zero, so a quiet day reads quiet; `null` is
// nothing to count, a dash.
function Cifra({ valor, tono }) {
  return (
    <td className={cn("px-3 text-right tabular-nums", valor ? cn("font-semibold", tono) : "text-brinks-muted-foreground")}>
      {valor == null ? "—" : valor}
    </td>
  );
}

// The day's documents: a dot in its state's colour and the document's name.
function Documentos({ documentos, nombres, fechaLabel }) {
  if (!documentos || !documentos.length) return <span className="text-brinks-muted-foreground">—</span>;
  return (
    <ul className="flex flex-wrap items-center gap-x-3 gap-y-0.5" aria-label={`Documentos del ${fechaLabel}`}>
      {documentos.map((d) => (
        <li key={d.clave} title={ETIQUETA_DOCUMENTO[d.estado]} className="inline-flex min-w-0 items-center gap-1.5 text-[11.5px] text-brinks-muted-foreground">
          <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", PUNTO_DOCUMENTO[d.estado])} aria-hidden="true" />
          <span className="truncate">{nombreDeFuente(nombres, d.clave)}</span>
          <span className="sr-only">: {ETIQUETA_DOCUMENTO[d.estado]}</span>
        </li>
      ))}
    </ul>
  );
}

export default PanelCierresPorFecha;
